import styles from "./style.module.scss";
import cx from "classnames";
import { ReactNode, useEffect, useState } from "react";
import { Button, Col, Input, InputGroup, InputGroupText, Row, Table } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { DocumentSummaryProps, removePaymentWay, setPaymentWayPropertyData } from "@/store/slices/eDocumentSlides";
import { EDocumentPaymentWayDataType } from "@/formTypes/EDocumentPaymentWayDataType";
import { formatMoneyAmount, generatePaymentWayOptions } from "@utils/EDocument";
import { BankAccountApi } from "@/api/BankAccountApi";
import { CreditOperatorApi } from "@/api/CreditOperatorApi";
import { CARD_PAYMENT_WAY_CODE, CHEQUE_PAYMENT_WAY_CODE, DIGITAL_PLATFORM_PAYMENT_WAY_CODE, SINPE_PAYMENT_WAY_CODE, TRANSFER_PAYMENT_WAY_CODE } from "@utils/Constants";

type EdocumentPaymentWayListProps = {
  documentSummary?: DocumentSummaryProps,
  montoInicial?: number
}

const EdocumentPaymentWayList = (props: EdocumentPaymentWayListProps) => {
  const { documentSummary, montoInicial = 0 } = props
  const dispatch = useAppDispatch();
  const documentPaymentWays = useAppSelector((state) => state.eDocument.documentPaymentWays) || [];
  const currency = useAppSelector((state) => state.currency) || [];
  const [localInputValues, setLocalInputValues] = useState<Record<string, string>>({});
  const [creditOperators, setCreditOperators] = useState<Record<string, string>>({});
  const [bankAccounts, setBankAccount] = useState<Record<string, string>>({});
  const paymentWayOptions = generatePaymentWayOptions()

  const totalAPagar = montoInicial || documentSummary?.total

  useEffect(() => {
    const newValues: Record<string, string> = {};
    documentPaymentWays.forEach(p => {
      newValues[`forma_pago-${p.id}`] = p.forma_pago || "";
      newValues[`monto-${p.id}`] = p.monto?.toString() || "";
      newValues[`digitosTarjeta-${p.id}`] = p.digitosTarjeta?.toString() || "";
      newValues[`creditOperatorId-${p.id}`] = p.creditOperatorId || "";
      newValues[`numAprobacion-${p.id}`] = p.numAprobacion || "";
      newValues[`reference-${p.id}`] = p.reference || "";
      newValues[`bankAccountId-${p.id}`] = p.bankAccountId || "";
    });
    setLocalInputValues(newValues);
    loadData()
  }, [documentPaymentWays]);

  const loadData = async () => {
    const backAccountOptions = await BankAccountApi.optionsList();
    const creditOperatorOptions = await CreditOperatorApi.optionsList();
    setBankAccount(backAccountOptions)
    setCreditOperators(creditOperatorOptions)
  }

  const updatePaymentWay = (id: string, propName: keyof EDocumentPaymentWayDataType, e: React.KeyboardEvent<HTMLInputElement>, dataType: "string" | "number") => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();

      const value = (e.target as HTMLInputElement).value;
      let finalValue: any = value;

      if (dataType === "number") {
        const parsed = Number(value);
        if (isNaN(parsed) || parsed < 0) return;
        finalValue = parsed;
      }

      dispatch(setPaymentWayPropertyData({ id, property: propName, value: finalValue }));
    }
  }

  const renderPaymentRows = (): ReactNode[] => {
    return documentPaymentWays.map(p => {

      const formaPago = localInputValues[`forma_pago-${p.id}`] || "";
      const digitosTarjeta = localInputValues[`digitosTarjeta-${p.id}`] || ""
      const numAprobacion = localInputValues[`numAprobacion-${p.id}`] || ""
      const reference = localInputValues[`reference-${p.id}`] || ""
      const bankAccountId = localInputValues[`bankAccountId-${p.id}`] || ""
      const creditOperatorId = localInputValues[`creditOperatorId-${p.id}`] || ""
      const bankAccountName = bankAccounts[bankAccountId] || ""
      const creditOperator = creditOperators[creditOperatorId] || ""

      return (
        <tr key={p.id} data-testid={`payment-row-${p.id}`}>
          <td>
            <Button data-testid={`payment-remove-${p.id}`} type="button" size="sm" color="danger" onClick={() => dispatch(removePaymentWay(`${p.id}`))}>
              <FontAwesomeIcon icon={faXmark} />
            </Button>
          </td>
          <td>
            {
              paymentWayOptions[formaPago]
            }
          </td>
          <td>
            {formaPago == `${CARD_PAYMENT_WAY_CODE}` && (
              <Input
                type="number"
                value={digitosTarjeta}
                onChange={e => setLocalInputValues({ ...localInputValues, [`digitosTarjeta-${p.id}`]: e.target.value })}
                onKeyDown={e => updatePaymentWay(`${p.id}`, "digitosTarjeta", e, "number")}
                onBlur={(e) => onInputBlur(e, digitosTarjeta)}
              />
            )}
          </td>
          <td>
            {formaPago == `${CARD_PAYMENT_WAY_CODE}` && (
              <Input
                type="text"
                value={numAprobacion}
                onChange={e => setLocalInputValues({ ...localInputValues, [`numAprobacion-${p.id}`]: e.target.value })}
                onKeyDown={e => updatePaymentWay(`${p.id}`, "numAprobacion", e, "string")}
                onBlur={(e) => onInputBlur(e, numAprobacion)}
              />
            )}
            {[
              `${CHEQUE_PAYMENT_WAY_CODE}`,
              `${TRANSFER_PAYMENT_WAY_CODE}`,
              `${SINPE_PAYMENT_WAY_CODE}`,
              `${DIGITAL_PLATFORM_PAYMENT_WAY_CODE}`
            ].includes(formaPago) && (
                <Input
                  type="text"
                  value={reference}
                  onChange={e => setLocalInputValues({ ...localInputValues, [`reference-${p.id}`]: e.target.value })}
                  onKeyDown={e => updatePaymentWay(`${p.id}`, "reference", e, "string")}
                  onBlur={(e) => onInputBlur(e, reference)}
                />
              )}
          </td>
          <td>

          </td>
          <td>
            {bankAccountName}
            {creditOperator}
          </td>
          <td>
            <InputGroup>
              <InputGroupText>
                {currency.currencySymbol}
              </InputGroupText>
              <Input
                type="number"
                value={localInputValues[`monto-${p.id}`] || ""}
                onChange={e => setLocalInputValues({ ...localInputValues, [`monto-${p.id}`]: e.target.value })}
                onKeyDown={e => updatePaymentWay(`${p.id}`, "monto", e, "number")}
                onBlur={(e) => onInputBlur(e, localInputValues[`monto-${p.id}`] || "")}
              />
            </InputGroup>
          </td>
        </tr>
      )
    })
  }
  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>, currentValue: any) => {
    e.target.value = currentValue
  }

  const renderSummary = (): ReactNode => {
    const paymentTotal = documentPaymentWays.reduce((a, v) => a + Number(v.monto || 0), Number(0))

    const moneyBack = paymentTotal - Number(totalAPagar || 0)
    return (
      <Row>
        <Col md={{
          offset: 6,
          size: 6
        }}>
          <hr />
          <Row>
            <Col md={{
              offset: 1,
              size: 5
            }}>
              <h5>
                <b>Total Factura</b>
              </h5>
            </Col>
            <Col className="text-end" md={{
              size: 5
            }}>
              <h4>
                <b data-testid="document-total" data-total={totalAPagar}> {`${currency.currencySymbol} ${formatMoneyAmount(totalAPagar)}`}</b>
              </h4>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col md={{
              offset: 1,
              size: 5
            }}>
              <h5>
                <b>Vuelto</b>
              </h5>
            </Col>
            <Col className="text-end" md={{
              size: 5
            }}>
              <h4>
                <b> {`${currency.currencySymbol} ${formatMoneyAmount(moneyBack)}`}</b>
              </h4>
            </Col>
          </Row>
        </Col>

      </Row>
    )

  }

  return (
    <>
      <Table className={styles.productTitle} striped size="sm">
        <thead className={styles.tableHeader}>
          <tr>
            <th></th>
            <th>Forma Pago</th>
            <th>Digitos Tarjeta</th>
            <th>Num Aprobacion / Referencia</th>
            <th># Nota Debito</th>
            <th>Operador / Banco</th>
            <th>Monto</th>
          </tr>
        </thead>
        <tbody>
          {renderPaymentRows()}
        </tbody>
      </Table>

      {documentSummary && renderSummary()}
    </>
  )
}

export default EdocumentPaymentWayList;